import { Box, Typography, LinearProgress, Alert, Collapse, IconButton, Tooltip, Chip } from '@mui/material';
import { CloudUpload, ExpandMore, ExpandLess, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AlphaAngle, BatchProgressEvent, BatchUploadResult } from '../../../types/photo';
import * as photoService from '../../../services/photoService';
import { AlphaAngleDialog } from './AlphaAngleDialog';

interface Props {
  workOrderDetailKey: string;
  onComplete: () => void;
}

// REQ-2026-0009: accept HEIC (iPhone default) alongside JPEG/PNG. Backend
// converts HEIC → JPEG on upload, applies EXIF rotation, and strips GPS.
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB hard ceiling (10–50 MB warns server-side)
const STEP_ICONS = [
  { key: 'uploading', label: 'Upload' },
  { key: 'processing', label: 'Auto-crop' },
  { key: 'saving', label: 'Save' },
  { key: 'done', label: 'Done' },
];
const STEP_ORDER = ['uploading', 'processing', 'saving', 'done', 'error', 'complete'];

export const BatchUploadZone = ({ workOrderDetailKey, onComplete }: Props) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<BatchProgressEvent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BatchUploadResult[] | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const validateFiles = useCallback((fileList: FileList | File[]): File[] | null => {
    const files = Array.from(fileList);
    if (files.length > 12) { setError('Maximum 12 photos per batch upload.'); return null; }
    // REQ-2026-0009: HEIC is now accepted; backend converts to JPEG. Some
    // browsers send HEIC with mimetype='' or application/octet-stream so we
    // accept either matching MIME or matching extension.
    const invalid = files.find((f) => {
      const name = f.name.toLowerCase();
      const extOk = ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
      const mimeOk = ACCEPTED_TYPES.includes(f.type);
      return !extOk && !mimeOk;
    });
    if (invalid) {
      setError(`Unsupported file type: ${invalid.name}. Accepted formats: JPEG, PNG, HEIC.`);
      return null;
    }
    const tooLarge = files.find((f) => f.size > MAX_FILE_SIZE);
    if (tooLarge) { setError(`File too large: ${tooLarge.name} (max 50 MB).`); return null; }
    setError(null);
    return files;
  }, []);

  const handleFiles = useCallback((fileList: FileList | File[]) => {
    const valid = validateFiles(fileList);
    if (valid) setPendingFiles(valid);
  }, [validateFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleConfirm = useCallback(async (alphaAngle: AlphaAngle) => {
    if (!pendingFiles) return;
    setPendingFiles(null);
    setUploading(true);
    setProgress(null);
    setError(null);
    setResults(null);

    try {
      const res = await photoService.batchUpload(
        workOrderDetailKey, pendingFiles, alphaAngle,
        (evt) => setProgress(evt),
      );
      setResults(res);
      const failed = res.filter((r) => r.processingStatus === 'failed');
      if (failed.length > 0) {
        // BUG-2026-0205: "failed" reads as "the app did nothing". It kept the
        // upload untouched — which is the right outcome for photos that were
        // already cropped offline (only one green marker left in frame).
        setError(`${failed.length} photo(s) kept exactly as uploaded — the scale markers weren't found. Already-cropped photos need no action; otherwise use "Crop manually".`);
      }
      onComplete();
    } catch (err: any) {
      setError(err.message || 'Batch upload failed');
    } finally {
      setUploading(false);
      setProgress(null);
    }
  }, [pendingFiles, workOrderDetailKey, onComplete]);

  const total = progress?.total || pendingFiles?.length || 0;
  // index = completed count; for in-progress events add partial weight
  const isInProgress = progress?.type === 'uploading' || progress?.type === 'processing' || progress?.type === 'saving';
  const SUB_WEIGHT: Record<string, number> = { uploading: 0.5, processing: 1.5, saving: 2.5 };
  const extraWeight = isInProgress ? (SUB_WEIGHT[progress!.type] || 0) : 0;
  const completed = (progress?.index ?? 0) + extraWeight;
  const isComplete = progress?.type === 'complete' || (results !== null && !uploading);
  const basePct = isComplete ? 100 : (total > 0 ? Math.min(95, Math.round((completed / total) * 100)) : 0);

  // Slow creep: when progress stalls, gradually increase displayed % (max +4% above basePct, capped at 98)
  const [displayPct, setDisplayPct] = useState(0);
  const creepRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Jump to base immediately when real progress arrives
    setDisplayPct((prev) => Math.max(prev, basePct));
    // Clear old creep timer and start new one
    if (creepRef.current) clearInterval(creepRef.current);
    if (uploading && basePct < 98) {
      creepRef.current = setInterval(() => {
        setDisplayPct((prev) => {
          const cap = Math.min(basePct + 4, 98);
          return prev < cap ? prev + 0.5 : prev;
        });
      }, 800);
    }
    return () => { if (creepRef.current) clearInterval(creepRef.current); };
  }, [basePct, uploading]);

  // Reset when starting a new upload (not when completing)
  useEffect(() => {
    if (uploading) setDisplayPct(0);
  }, [uploading]);

  const pct = Math.round(displayPct);
  const pctDisplay = progress?.type === 'done' || progress?.type === 'error'
    ? Math.round((progress.index / total) * 100) : pct;
  const failedCount = results?.filter((r) => r.processingStatus === 'failed').length ?? 0;
  const successCount = results?.filter((r) => r.processingStatus === 'success').length ?? 0;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600} color="text.secondary">Batch Upload</Typography>
        <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={expanded}>
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          sx={{
            border: `1.5px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: 1.5, px: 3, py: 1.5, textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer',
            backgroundColor: dragOver ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
            transition: 'all 0.2s',
            pointerEvents: uploading ? 'none' : 'auto',
          }}
        >
          <input ref={fileInputRef} type="file" multiple
            accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
            style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files) handleFiles(e.target.files); e.target.value = ''; }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <CloudUpload sx={{ fontSize: 24, color: 'text.secondary' }} />
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Drag &amp; drop test photos here, or click to browse
              </Typography>
              <Typography variant="caption" color="text.secondary">
                JPEG, PNG, HEIC. Up to 12 photos per batch, 50 MB max each.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Real-time progress */}
        {uploading && progress && progress.type !== 'complete' && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {progress.index} of {total} complete
                {total > 1 && <Typography component="span" variant="body2" color="text.secondary"> (processing 3 in parallel)</Typography>}
              </Typography>
              <Chip label={`${pctDisplay}%`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700, minWidth: 48 }} />
            </Box>
            <LinearProgress variant="determinate" value={pct} sx={{ mb: 1, height: 6, borderRadius: 3 }} />
            {progress.type === 'done' || progress.type === 'error' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {progress.type === 'done'
                  ? <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                  : <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                <Typography variant="caption" color="text.secondary">
                  <strong>{progress.fileName}</strong> {'\u2192'} {progress.stage}/{progress.position}
                  {progress.type === 'error' && ` — ${progress.error}`}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                {STEP_ICONS.map((step) => {
                  const isCurrent = progress.type === step.key;
                  const isDone = STEP_ORDER.indexOf(progress.type) > STEP_ORDER.indexOf(step.key);
                  return (
                    <Chip key={step.key} size="small" label={step.label}
                      icon={isDone ? <CheckCircle sx={{ fontSize: '14px !important' }} /> : undefined}
                      variant={isCurrent ? 'filled' : 'outlined'}
                      color={isDone ? 'success' : isCurrent ? 'primary' : 'default'}
                      sx={{ fontSize: '0.65rem', height: 22, opacity: isDone || isCurrent ? 1 : 0.4 }}
                    />
                  );
                })}
                {progress.fileName && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    {progress.fileName}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {uploading && !progress && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Sending files to server...</Typography>
            <LinearProgress sx={{ mt: 0.5 }} />
          </Box>
        )}

        {results && !uploading && (
          <Alert severity={failedCount > 0 ? 'warning' : 'success'} sx={{ mt: 1, py: 0 }}>
            {successCount} auto-cropped.{failedCount > 0 && ` ${failedCount} kept as uploaded (scale markers not found).`}
          </Alert>
        )}

        {error && !results && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{error}</Alert>}
      </Collapse>

      <AlphaAngleDialog
        open={pendingFiles !== null}
        fileCount={pendingFiles?.length ?? 0}
        onConfirm={handleConfirm}
        onCancel={() => setPendingFiles(null)}
      />
    </Box>
  );
};
